import { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

const LanguageContext = createContext(null);
export const SUPPORTED_LANGS = ["ar", "en"];

/**
 * English is the canonical site root (no `/en/` prefix). Arabic lives under
 * its own subtree at `/ar/*`. This helper keeps every URL builder honest:
 *
 *   langPrefix("en") === ""     ->  paths look like  "/about", "/tools"
 *   langPrefix("ar") === "/ar"  ->  paths look like  "/ar/about", "/ar/tools"
 *
 * Always concatenate as `${langPrefix(lang)}${pathStartingWithSlash}` and
 * fall back to "/" for the bare home (since "" is not a valid <Link to>).
 */
export function langPrefix(lang) {
  return lang === "ar" ? "/ar" : "";
}

/**
 * Default language. English is the canonical homepage at `/`, so anything
 * outside the explicit `/ar` subtree is treated as English.
 */
export const DEFAULT_LANG = "en";

/** Derive language from the URL pathname.
 *  - `/ar` or `/ar/...` → "ar"
 *  - everything else (including `/`) → "en"
 */
export function getLangFromPath(pathname) {
  if (typeof pathname !== "string") return DEFAULT_LANG;
  if (/^\/ar(?=\/|$)/.test(pathname)) return "ar";
  return "en";
}

/**
 * Lang is derived from the URL pathname. No state, no localStorage.
 * This ensures AR and EN are two separate route trees, not an in-page toggle.
 */
export function LanguageProvider({ children }) {
  const { pathname } = useLocation();
  const lang = getLangFromPath(pathname);
  const dir = lang === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("lang", lang);
    html.setAttribute("dir", dir);
  }, [lang, dir]);

  const value = useMemo(() => ({ lang, dir }), [lang, dir]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within <LanguageProvider>");
  return ctx;
}

/** Resolve a value that may be either a plain string or a {ar, en} dict. */
export function pickLang(value, lang) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value[lang] || value.ar || value.en || "";
  return String(value);
}

/**
 * Mirror a path under a different language while preserving the rest.
 *
 *   switchLangPath("/about", "ar")           -> "/ar/about"
 *   switchLangPath("/ar/articles/foo", "en") -> "/articles/foo"
 *   switchLangPath("/", "ar")                -> "/ar"
 *   switchLangPath("/ar", "en")              -> "/"
 */
export function switchLangPath(pathname, newLang) {
  // Strip the Arabic prefix (if present) to recover the path-after-language.
  let rest = (pathname || "/").replace(/^\/ar(?=\/|$)/, "");
  if (rest === "" || rest === "/") {
    return newLang === "ar" ? "/ar" : "/";
  }
  if (!rest.startsWith("/")) rest = `/${rest}`;
  return newLang === "ar" ? `/ar${rest}` : rest;
}
